
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Bill } from "../../types";

export function useBillsFetch(
  setLoading: (loading: boolean) => void,
  setBills: (bills: Bill[]) => void,
  setFetchComplete: (complete: boolean) => void,
  fetchComplete: boolean
) {
  const { toast } = useToast();

  const fetchBills = useCallback(async () => {
    // Skip if we've already completed a fetch
    if (fetchComplete) {
      return;
    }
    
    try {
      setLoading(true);
      console.log('Fetching bills...');
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user");
      }

      const { data: schoolUser } = await supabase
        .from('school_users')
        .select('school_id')
        .eq('email', user.email)
        .maybeSingle();

      if (!schoolUser) {
        setBills([]);
        setFetchComplete(true);
        return;
      }

      const { data: client } = await supabase
        .from('clients')
        .select('id')
        .eq('school_id', schoolUser.school_id)
        .maybeSingle();

      if (!client) {
        setBills([]);
        setFetchComplete(true);
        return;
      }

      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('id')
        .eq('client_id', client.id);

      if (!subscriptions || subscriptions.length === 0) {
        setBills([]);
        setFetchComplete(true);
        return;
      }

      const subscriptionIds = subscriptions.map(sub => sub.id);

      const { data: billsData, error } = await supabase
        .from('bills')
        .select(`
          id,
          amount,
          tax,
          discount,
          discount_type,
          status,
          due_date,
          description,
          transaction_number,
          bill_component_id,
          subscription_id,
          plan:plans (
            id,
            name,
            price,
            product:products (
              id,
              name
            )
          )
        `)
        .in('subscription_id', subscriptionIds)
        .order('due_date', { ascending: false });

      if (error) throw error;

      // Transform the data to match the Bill type with explicit type casting
      const formattedBills = billsData?.map(bill => {
        // Calculate discount_percentage only if discount_type is 'percentage'
        const discountPercentage = bill.discount_type === 'percentage' ? 
          parseFloat(bill.discount.toString()) : null;
          
        return {
          ...bill,
          discount_percentage: discountPercentage
        };
      }) as Bill[] || [];

      console.log('Fetched bills:', formattedBills);
      setBills(formattedBills);
      setFetchComplete(true);
    } catch (error) {
      console.error('Error fetching bills:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch bills",
      });
      setBills([]);
      setFetchComplete(true);
    } finally {
      setLoading(false);
    }
  }, [fetchComplete, toast, setBills, setLoading, setFetchComplete]);

  return { fetchBills };
}
